import { getPlayerRowsAndAddEmptyIfNeeded } from "@ihs-widget/components/StatsConsole/Widgets/LineupWidget/hooks/getPlayerRows";

import OnePlayer from "@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/OnePlayer";
import DiamondFormationPlayers from "@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/DiamondFormationPlayers";
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import FilterButton from "../../common/FilterButton";
import { LeftArrowInChipsFilters } from '@ihs-widget/components/StatsConsole/Layout/parts/ChipsFiltersarrows/LeftArrowInChipsFilters';
import { RightArrowInChipsFilters } from '@ihs-widget/components/StatsConsole/Layout/parts/ChipsFiltersarrows/RightArrowInChipsFilters';
import { useTheme } from '@ihs-widget/stores/ThemeContext';
import { useLanguage } from "@ihs-widget/utils/hooks/useLanguage";
import { getEnumKeyByValue, getIsMobileDevice } from "@ihs-widget/utils/functions";
import { type IState, StateContext } from "../../../../../state/stateContext"; // TODO: import from @ihs-widget/state/stateContext, not works why?
import { T, SCLineupPlayersActiveFilterName, Version } from "@ihs-widget/utils/enums";
import React from 'react';
import OnePlayerEmpty from "@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/OnePlayerEmpty";
import { useTranslations } from "../../../../../translation/TranslationContext";
import { useVersion } from "@ihs-widget/utils/hooks/useVersion";

export interface IPlayer {
    name: string;
    first_name?: string;
    last_name?: string;
    number: number;
    position: string;
    position_label: string;
    preferredFoot: string;
    substitutionTime: {
        in: number | null;
        out: number | null;
    } | null | undefined;
    goal: number | null;
    own_goal: number | null;
    injury: boolean | null;
    yellowCard: number | null;
    secondYellowCard: number | null;
    redCard: boolean | null;
    assist: number | null;
    xGoals: number | null;
    shots: number | null;
    ballTouches: number | null;
    age: number | null;
    isCaptain: boolean | null;
    isCurrentlyOnField: boolean | null;
}

interface IPlayerRowProps {
    homeFormation: string | null;
    awayFormation: string | null;
    homePlayers: IPlayer[];
    awayPlayers: IPlayer[];
    homeFormationType?: string | null;
    awayFormationType?: string | null;
}

export interface PlayersRefContext {
  playersRef: React.RefObject<HTMLDivElement | null> | null;
}

export const PlayersRefContext = React.createContext<PlayersRefContext>({ playersRef: null });

const Players = ({ homeFormation, awayFormation, homePlayers, awayPlayers, homeFormationType, awayFormationType }: IPlayerRowProps) => {
    const homePlayerRows = useMemo(() => {
        const result = getPlayerRowsAndAddEmptyIfNeeded(homePlayers, homeFormation);
        return result;
    }, [homePlayers, homeFormation]);

    const awayPlayerRows = useMemo(() => {
        return getPlayerRowsAndAddEmptyIfNeeded(awayPlayers, awayFormation);
    }, [awayPlayers, awayFormation]);

    const { rows: playerRows, rowLengths } = homePlayerRows;
    const { rows: playerRowsAway, rowLengths: rowLengthsAway } = awayPlayerRows;

    const [isHovered, setIsHovered] = useState(false);
    const [isMenuArrowsVisible, setIsMenuArrowsVisible] = useState(false);
    const [isLeftArrowDisabled, setIsLeftArrowDisabled] = useState(true);
    const [isRightArrowDisabled, setIsRightArrowDisabled] = useState(true);
    const [isMenuItemsScrollable, setIsMenuItemsScrollable] = useState(false);
    const { isV1, isV2 } = useVersion();  

    const playersRef = useRef<HTMLDivElement>(null);

    const ctx = useContext(StateContext);
    const { stContext, setStContext } = ctx || {};
    const { lineupTabActiveFilter, lineupTabActiveFilterName, statsConsoleIframeWidth: iframeWidth } = stContext || {};


    const menuItemsLineupRef = useRef<HTMLDivElement>(null);
    const menuItemsLineupContainerRef = useRef<HTMLDivElement>(null);

    const language = useLanguage();
    const t = useTranslations(language);

    const { theme, version } = useTheme();

    const SCLinupPlayersActiveFilter = useMemo(() => ({
        XGOALS: t[`InHouseStats.LineupXGoals`],
        SHOTS: t['InHouseStats.LineupShots'],
        BALL_TOUCHES: t['InHouseStats.LineupBallTouches'],
        NULL: '',
    }), [t]);

    type SCLinupPlayersActiveFilterKey = keyof typeof SCLinupPlayersActiveFilter;

    const setActiveFilter = (filter: SCLinupPlayersActiveFilterKey | null) => {
        setStContext((prev: IState) => ({
            ...prev,
            lineupTabActiveFilter: filter,
            lineupTabActiveFilterName: getActiveFilterName(filter)
        }));
    };

    const getActiveFilterName = (filter: SCLinupPlayersActiveFilterKey | null): SCLineupPlayersActiveFilterName => {
        switch (filter) {
            case getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.XGOALS):
                return SCLineupPlayersActiveFilterName.XGOALS;
            case getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.SHOTS):
                return SCLineupPlayersActiveFilterName.SHOTS;
            case getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.BALL_TOUCHES):
                return SCLineupPlayersActiveFilterName.BALL_TOUCHES;
            default:
                return SCLineupPlayersActiveFilterName.XGOALS;
        }
    };

    const onClickLeftArrow = () => {
        const menuItemsContainer = menuItemsLineupRef.current;
        if (menuItemsContainer) {
            const currentScroll = menuItemsContainer.scrollLeft;
            const scrollAmount = Math.min(40, currentScroll);
            menuItemsContainer.scrollLeft -= scrollAmount;
        }
    }

    const onClickRightArrow = () => {
        const menuItemsContainer = menuItemsLineupRef.current;
        if (menuItemsContainer) {
            const maxScroll = menuItemsContainer.scrollWidth - menuItemsContainer.clientWidth;
            const currentScroll = menuItemsContainer.scrollLeft;
            const remainingScroll = maxScroll - currentScroll;
            const scrollAmount = Math.min(40, remainingScroll);
            menuItemsContainer.scrollLeft += scrollAmount;
        }
    }

    const handleFilterXGOALSClick = () => {
        setStContext((prev: IState) => ({
            ...prev,
            activeTooltipPlayerId: null
        }));
        
        if (lineupTabActiveFilter !== getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.XGOALS)) { 
            setActiveFilter(getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.XGOALS) as SCLinupPlayersActiveFilterKey)
        } else {
            setActiveFilter(getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.NULL) as SCLinupPlayersActiveFilterKey)
        }
    };

    const handleFilterSHOTSClick = () => {
        setStContext((prev: IState) => ({
            ...prev,
            activeTooltipPlayerId: null
        }));
        
        if (lineupTabActiveFilter !== getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.SHOTS)) {
            setActiveFilter(getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.SHOTS) as SCLinupPlayersActiveFilterKey)
        } else {
            setActiveFilter(getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.NULL) as SCLinupPlayersActiveFilterKey)
        }
    };

    const handleFilterBALL_TOUCHESClick = () => {
        setStContext((prev: IState) => ({
            ...prev,
            activeTooltipPlayerId: null
        }));
        
        if (lineupTabActiveFilter !== getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.BALL_TOUCHES)) {
            setActiveFilter(getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.BALL_TOUCHES) as SCLinupPlayersActiveFilterKey)
        } else {
            setActiveFilter(getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.NULL) as SCLinupPlayersActiveFilterKey)
        }
    };

    const handleScroll = () => {
        if (menuItemsLineupRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = menuItemsLineupRef.current;
            setIsLeftArrowDisabled(scrollLeft === 0);
            setIsRightArrowDisabled(scrollLeft + clientWidth >= scrollWidth);
        }
    };

    useEffect(() => {
        const menuItems = menuItemsLineupRef.current;
        if (menuItems) {
            menuItems.addEventListener('scroll', handleScroll);
            handleScroll(); 
        }
        return () => {
            if (menuItems) {
                menuItems.removeEventListener('scroll', handleScroll);
            }
        };
    }, [window.innerWidth]);

    useEffect(() => {
        const isMobileDevice = getIsMobileDevice();
        if (isMobileDevice) {
            setIsMenuArrowsVisible(false); 
        } else {
            if (isMenuItemsScrollable) {
                setIsMenuArrowsVisible(true);
            } else {
                setIsMenuArrowsVisible(false);
            }
        }
    }, [isMenuItemsScrollable])

    useEffect(() => {
        const checkScrollability = () => {
            if (menuItemsLineupContainerRef.current && menuItemsLineupRef.current) {
                const itemsWidth = menuItemsLineupRef.current.scrollWidth;
                const windowWidth = window.innerWidth;
                setIsMenuItemsScrollable(itemsWidth > windowWidth);
            } else {
                setIsMenuItemsScrollable(false);
            }
        };
    
        checkScrollability();
        window.addEventListener('resize', checkScrollability);
    
        return () => {
            window.removeEventListener('resize', checkScrollability);
        };
    }, [language ]);

    useEffect(() => {
    }, [lineupTabActiveFilter]);

    useEffect(() => {
    }, [homeFormation, awayFormation]);

    if (!homePlayers || !awayPlayers) {
        return null;
    }

    return (
        <div ref={playersRef} className={`w-full h-full ${isV1 ? 'pt-[58px]' : 'mt-[94px]'}`} >
            <PlayersRefContext.Provider value={{ playersRef }}>
                <div className={`absolute left-0 right-0 h-[58px] w-full z-30 ${isV1 ? 'top-0' : 'top-[36px]'}`} 
                    ref={menuItemsLineupContainerRef}>
                <div className=' flex flex-row justify-center items-center gap-[3px] w-full select-none' 
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}>
                            {isMenuArrowsVisible && 
                                <LeftArrowInChipsFilters
                                    color={theme === T.LIGHT ? (isLeftArrowDisabled ? '#111111' : '#00531D') : (isLeftArrowDisabled ? '#111111' : '#00531D')}  
                                    onClick={onClickLeftArrow} 
                                    isVisibleOnHover={isHovered}
                                    isArrowHidden={isLeftArrowDisabled}
                                    
                                />
                            }
                            <div ref={menuItemsLineupRef} 
                                 className={`pl-[8px] flex min-w-full items-center mt-[12px] mb-[12px]
                                            overflow-x-scroll menuItemsStats-disable-scrollbar z-10 
                                            [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]
                                            ${isMenuArrowsVisible && 'justify-start'}
                                            ${!isMenuArrowsVisible && isMenuItemsScrollable && 'justify-start'}
                                            ${!isMenuArrowsVisible && !isMenuItemsScrollable && 'justify-center'}`}>
                                <FilterButton 
                                    text={SCLinupPlayersActiveFilter.XGOALS} 
                                    isActive={lineupTabActiveFilter === getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.XGOALS)} 
                                    onClick={handleFilterXGOALSClick}
                                    additionalClassName={`mr-[8px]`}
                                    id="filter-xgoals"
                                />
                                <FilterButton 
                                    text={SCLinupPlayersActiveFilter.SHOTS} 
                                    isActive={lineupTabActiveFilter === getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.SHOTS)} 
                                    onClick={handleFilterSHOTSClick} 
                                    additionalClassName={`mr-[8px]`}
                                    id="filter-shots"
                                />
                                <FilterButton 
                                    text={SCLinupPlayersActiveFilter.BALL_TOUCHES} 
                                    isActive={lineupTabActiveFilter === getEnumKeyByValue(SCLinupPlayersActiveFilter, SCLinupPlayersActiveFilter.BALL_TOUCHES)} 
                                    onClick={handleFilterBALL_TOUCHESClick} 
                                    additionalClassName={`mr-[8px]`}
                                    id="filter-ball-touches"
                                />
                            </div>
                             {isMenuArrowsVisible && 
                                <RightArrowInChipsFilters 
                                    color={theme === T.LIGHT ? (isRightArrowDisabled ? '#111111' : '#00531D') : (isRightArrowDisabled ? '#111111' : '#00531D')} 
                                    onClick={onClickRightArrow} 
                                    isVisibleOnHover={isHovered}
                                    isArrowHidden={isRightArrowDisabled}
                                />
                            }
                        </div>
                </div>
                {playerRows.map((row, rowIndex) => {
                    if (rowLengths[rowIndex] === 1) {
                        return (
                            <div key={rowIndex} className={`flex justify-center mb-[8px] w-full flex-nowrap
                                ${iframeWidth && iframeWidth < 349 ? 'gap-px' : 'gap-[4px]'}
                            `}>
                                {row.map((player, playerIndex) => (
                                        <OnePlayer 
                                            team="home" 
                                            key={`${rowIndex}-${playerIndex}-${player?.name}-${player?.number}-home`} 
                                            width={iframeWidth && iframeWidth < 349 ? `${iframeWidth - 24}px` : '343px'} 
                                            player={player} 
                                            id={`${rowIndex}-${playerIndex}-${player?.name}-${player.number}-home`}
                                            longName={true}
                                            isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                                            isLineup={true}
                                        />
                                ))}
                            </div>
                        )
                    } else if (rowLengths[rowIndex] === 2) {
                        return (
                            <div key={rowIndex} className={`flex justify-center mb-[8px] w-full flex-nowrap
                                ${iframeWidth && iframeWidth < 349 ? 'gap-px' : 'gap-[4px]'}
                            `}>
                                {row.map((player, playerIndex) => (
                                    <OnePlayer 
                                        team="home" 
                                        key={`${rowIndex}-${playerIndex}-${player?.name}-${player.number}-home`}
                                        width={iframeWidth && iframeWidth < 349 ? `${(iframeWidth - 24) / 2}px` : '169.5px'} 
                                        // width={'169.5px'} 
                                        player={player} 
                                        id={`${rowIndex}-${playerIndex}-${player?.name}-${player.number}-home`}
                                        longName={true}
                                        isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                                        isLineup={true}
                                    />
                                ))}
                            </div>
                        )
                    } else if (rowLengths[rowIndex] === 3) {
                        return (
                            <div key={rowIndex} className={`flex justify-center mb-[8px] w-full flex-nowrap
                                ${iframeWidth && iframeWidth < 349 ? 'gap-px' : 'gap-[4px]'}
                            `}>
                                {row.map((player, playerIndex) => (
                                    <OnePlayer 
                                        team="home" 
                                        key={`${rowIndex}-${playerIndex}-${player?.name}-${player.number}-home`} 
                                        width={iframeWidth && iframeWidth < 349 ? `${(iframeWidth - 24) / 3}px` : '112px'} 
                                        // width={"112px"} 
                                        player={player} 
                                        id={`${rowIndex}-${playerIndex}-${player?.name}-${player.number}-home`} 
                                        longName={true}
                                        isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                                        isLineup={true}
                                    />
                                ))}
                            </div>
                        )
                    } else if (rowLengths[rowIndex] === 4) {
                        const isDiamondFormation = homeFormationType === 'diamond' && rowIndex !== 0;
                        const formationParts = homeFormation?.split('-').map(Number) || [];
                        const isMidfieldLine = rowIndex > 0 && rowIndex <= formationParts.length;
                        
                        if (isDiamondFormation && isMidfieldLine) {
                            return <DiamondFormationPlayers players={row} rowIndex={rowIndex} team="home" iframeWidth={iframeWidth} />;
                        }
                        
                        return (
                            <div key={rowIndex} className={`flex justify-center mb-[8px] w-full flex-nowrap
                                ${iframeWidth && iframeWidth < 349 ? 'gap-px' : 'gap-[4px]'}
                            `}>
                                {row.map((player, playerIndex) => (
                                    <OnePlayer 
                                        team="home" 
                                        key={`${rowIndex}-${playerIndex}-${player?.name}-${player.number}-home`} 
                                        width={iframeWidth && iframeWidth < 349 ? `${(iframeWidth - 24) / 4}px` : '82.75px'} 
                                        // width={"82.75px"} 
                                        player={player} 
                                        id={`${rowIndex}-${playerIndex}-${player?.name}-${player.number}-home`} 
                                        longName={true}
                                        isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                                        isLineup={true}
                                    />
                                ))}
                            </div>
                        )
                    }
                    else if (rowLengths[rowIndex] === 5) {
                        return (
                            <div key={rowIndex} className={`flex justify-center mb-[8px] w-full flex-nowrap
                                ${iframeWidth && iframeWidth < 349 ? 'gap-px' : 'gap-[4px]'}
                            `}>
                                {row.map((player, playerIndex) => (
                                    <OnePlayer 
                                        team="home" 
                                        key={`${rowIndex}-${playerIndex}-${player?.name}-${player.number}-home`} 
                                        width={iframeWidth && iframeWidth < 349 ? `${(iframeWidth - 24) / 5}px` : '65.4px'} 
                                        // width={"65.4px"} 
                                        player={player} 
                                        id={`${rowIndex}-${playerIndex}-${player?.name}-${player.number}-home`} 
                                        longName={true}
                                        isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                                        isLineup={true}
                                    />
                                ))}
                            </div>
                        )
                    } else {
                        return (
                            <div key={rowIndex} className={`flex justify-center mb-[8px] w-full flex-nowrap
                                ${iframeWidth && iframeWidth < 349 ? 'gap-px' : 'gap-[4px]'}
                            `}>
                                    <OnePlayerEmpty
                                        team="home"
                                        width={iframeWidth && iframeWidth < 349 ? `${(iframeWidth - 24) / 1}px` : '1px'} 
                                    />
                            </div>
                        )
                    }
                })}
                {[...playerRowsAway].reverse().map((row, rowIndex) => {
                    const actualRowIndex = playerRowsAway.length - 1 - rowIndex;
                    const isGoalkeeper = rowIndex === playerRowsAway.length - 1;
                    const isFirstRowAfterGoalkeeper = rowIndex === playerRowsAway.length - 2;
                    
                    if (rowLengthsAway[actualRowIndex] === 1) {
                        return (
                            <div key={actualRowIndex} className={`${actualRowIndex} flex justify-center w-full flex-nowrap 
                                ${iframeWidth && iframeWidth < 349 ? 'gap-px' : 'gap-[4px]'}
                                ${isGoalkeeper ? (isV1 ? 'mt-[10px]' : 'mt-0') : ''}
                                ${isV2 ? 'mb-[4px]' : 'mb-[8px]'}
                                `}>
                                {row.map((player, playerIndex) => (
                                    <OnePlayer 
                                        team="away" 
                                        key={`${actualRowIndex}-${playerIndex}-${player?.name}-${player.number}-away`} 
                                        width={iframeWidth && iframeWidth < 349 ? `${iframeWidth - 24}px` : '343px'} 
                                        // width={'343px'} 
                                        player={player}
                                        id={`${actualRowIndex}-${playerIndex}-${player?.name}-${player.number}-away`}
                                        longName={true}
                                        isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                                        isLineup={true}
                                    />
                                ))}
                            </div>
                        )
                    } else if (rowLengthsAway[actualRowIndex] === 2) {
                        return (
                            <div key={actualRowIndex} className={`${actualRowIndex} flex justify-center  w-full flex-nowrap
                                ${iframeWidth && iframeWidth < 349 ? 'gap-px' : 'gap-[4px]'}
                                ${isV2 ? 'mb-[4px]' : 'mb-[8px]'}
                            `}>
                                {row.map((player, playerIndex) => (
                                    <OnePlayer 
                                        team="away" 
                                        key={`${actualRowIndex}-${playerIndex}-${player?.name}-${player.number}-away`} 
                                        width={iframeWidth && iframeWidth < 349 ? `${(iframeWidth - 24) / 2}px` : '169.5px'} 
                                        // width={"169.5px"} 
                                        player={player} 
                                        id={`${actualRowIndex}-${playerIndex}-${player?.name}-${player.number}-away  `} 
                                        longName={true}
                                        isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                                        isLineup={true}
                                    />
                                ))}
                            </div>
                        )
                    } else if (rowLengthsAway[actualRowIndex] === 3) {
                        return (
                            <div key={actualRowIndex} className={`${actualRowIndex} flex justify-center  w-full flex-nowrap
                                ${iframeWidth && iframeWidth < 349 ? 'gap-px' : 'gap-[4px]'}
                                ${isV2 ? 'mb-[4px]' : 'mb-[8px]'}
                            `}>
                                {row.map((player, playerIndex) => (
                                    <OnePlayer 
                                        team="away" 
                                        key={`${actualRowIndex}-${playerIndex}-${player?.name}-${player?.number}-away`} 
                                        width={iframeWidth && iframeWidth < 349 ? `${(iframeWidth - 24) / 3}px` : '112px'} 
                                        // width={"112px"} 
                                        player={player} 
                                        id={`${actualRowIndex}-${playerIndex}-${player?.name}-${player?.number}-away  `} 
                                        longName={true}
                                        isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                                        isLineup={true}
                                    />
                                ))}
                            </div>
                        )
                    } else if (rowLengthsAway[actualRowIndex] === 4) {
                        const isDiamondFormation = awayFormationType === 'diamond' && actualRowIndex !== 0;
                        const formationParts = awayFormation?.split('-').map(Number) || [];
                        const isMidfieldLine = actualRowIndex > 0 && actualRowIndex <= formationParts.length;
                        
                        if (isDiamondFormation && isMidfieldLine) {
                            return <DiamondFormationPlayers players={row} rowIndex={actualRowIndex} team="away" iframeWidth={iframeWidth} />;
                        }
                        
                        return (
                            <div key={actualRowIndex} className={`${actualRowIndex} flex justify-center  w-full flex-nowrap
                                ${iframeWidth && iframeWidth < 349 ? 'gap-px' : 'gap-[4px]'}
                                ${isV2 ? 'mb-[4px]' : 'mb-[8px]'}
                            `}>
                                {row.map((player, playerIndex) => (
                                    <OnePlayer 
                                        team="away" 
                                        key={`${actualRowIndex}-${playerIndex}-${player?.name}-${player.number}-away`} 
                                        width={iframeWidth && iframeWidth < 349 ? `${(iframeWidth - 24) / 4}px` : '82.75px'} 
                                        // width={"82.75px"} 
                                        player={player} 
                                        id={`${actualRowIndex}-${playerIndex}-${player?.name}-${player.number}-away  `} 
                                        longName={true}
                                        isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                                        isLineup={true}
                                    />
                                ))}
                            </div>
                        )
                    } else if (rowLengthsAway[actualRowIndex] === 5) {
                        return (
                            <div key={actualRowIndex} className={`${actualRowIndex} flex justify-center w-full flex-nowrap
                                ${iframeWidth && iframeWidth < 349 ? 'gap-px' : 'gap-[4px]'}
                                ${isV2 ? 'mb-[4px]' : 'mb-[8px]'}
                            `}>
                                {row.map((player, playerIndex) => (
                                    <OnePlayer 
                                        team="away" 
                                        key={`${actualRowIndex}-${playerIndex}-${player?.name}-${player.number}-away`} 
                                        width={iframeWidth && iframeWidth < 349 ? `${(iframeWidth - 24) / 5}px` : '65.4px'} 
                                        // width={"65.4px"} 
                                        player={player} 
                                        id={`${actualRowIndex}-${playerIndex}-${player?.name}-${player.number}-away  `} 
                                        longName={true}
                                        isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                                        isLineup={true}
                                    />
                                ))}
                            </div>
                        )
                    }
                    else {
                        return (
                            <div key={actualRowIndex} className={`${actualRowIndex} flex justify-center mb-[8px] w-full flex-nowrap
                            `}>
                                <OnePlayerEmpty
                                    team="away"
                                    width={iframeWidth && iframeWidth < 349 ? `${(iframeWidth - 24) / 1}px` : '1px'} 
                                    
                                />
                            </div>
                        )
                    }
                })}
            </PlayersRefContext.Provider>
        </div>
    )
}

export default Players;